import React, { useEffect, useMemo, useRef, useState } from 'react';
import Split from 'react-split';
import {
  FaSearch,
  FaSync,
  FaPlus,
  FaTrash,
  FaClipboardCheck,
  FaClipboardList,
  FaWarehouse,
  FaUndo,
  FaTimes,
  FaPrint,
  FaEnvelope,
  FaFileCsv,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaRedo,
  FaQuestionCircle
} from 'react-icons/fa';
import './PurchaseOrders.css';
import { showToast } from '../utils/toast';

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const API = API_BASE || '';
const DEFAULT_THRESHOLD = Number(import.meta.env.VITE_LOW_STOCK_THRESHOLD || 5) || 5;
const DEFAULT_LOW_LIMIT = 200;
const NO_IMAGE_PLACEHOLDER = '/no-image-placeholder.svg';
const MIN_COL_W = 80;
const LS_PO_COLWIDTHS_KEY = 'purchaseOrders.tableWidths.v1';
const LS_LOW_COLWIDTHS_KEY = 'purchaseOrders.lowTableWidths.v1';
const LS_BEST_COLWIDTHS_KEY = 'purchaseOrders.bestTableWidths.v1';
const LS_PO_SPLIT_KEY = 'purchaseOrders.split.v1';
const LS_PO_V_SPLIT_KEY = 'purchaseOrders.vsplit.v1';
const LS_PO_CLIENT_FILTER_KEY = 'purchaseOrders.clientFilter';
const LS_PO_LOW_INVENTORY_TAB_KEY = 'purchaseOrders.lowInventoryTab.v1';
const LS_PO_FORECAST_ENABLED_KEY = 'purchaseOrders.forecast.enabled.v1';
const LS_PO_FORECAST_LOOKBACK_KEY = 'purchaseOrders.forecast.lookbackDays.v1';
const LS_PO_FORECAST_REVIEW_KEY = 'purchaseOrders.forecast.reviewDays.v1';
const LS_PO_FORECAST_SAFETY_KEY = 'purchaseOrders.forecast.safetyDays.v1';

const PO_STATUS_OPTIONS = [
  { value: 1, label: 'Draft' },
  { value: 2, label: 'Ordered' },
  { value: 3, label: 'Receiving' },
  { value: 4, label: 'Received' },
  { value: 5, label: 'Cancelled' }
];
const STATUS_LABELS = PO_STATUS_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const shortfallForItem = (item) => {
  const threshold = Number(item?.lowThreshold ?? DEFAULT_THRESHOLD);
  const onHand = Number(item?.qtyUnitsOnHand ?? 0);
  const value = threshold - onHand;
  return Number.isFinite(value) ? Math.max(Math.round(value), 0) : 0;
};

const formatNumber = (value, opts = {}) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(undefined, opts);
};

const fetchJSON = async (url, options = {}) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {}
    throw new Error(message);
  }
  return res.json();
};

const PO_TABLE_COLS = [
  'actions',
  'poNumber',
  'client',
  'supplier',
  'warehouse',
  'notes',
  'lineCount',
  'totalQty',
  'totalReceived',
  'status',
  'expectedDate',
  'updatedAt',
  'convert'
];
const PO_COLUMN_LABELS = {
  actions: 'Actions',
  poNumber: 'PO #',
  client: 'Client',
  supplier: 'Supplier',
  warehouse: 'Warehouse',
  notes: 'Notes',
  lineCount: 'Lines',
  totalQty: 'Qty Ordered',
  totalReceived: 'Received',
  status: 'Status',
  expectedDate: 'Expected',
  updatedAt: 'Updated',
  convert: 'Convert'
};
const LOW_TABLE_COLUMNS = [
  'plan',
  'client',
  'sku',
  'onhand',
  'threshold',
  'shortfall',
  'orderqty'
];
const BEST_TABLE_COLUMNS = [
  'plan',
  'client',
  'sku',
  'sold',
  'avg',
  'onhand',
  'orderqty'
];
const LOW_COLUMN_LABELS = {
  plan: 'Plan',
  client: 'Client',
  sku: 'SKU / Description',
  onhand: 'On Hand',
  threshold: 'Threshold',
  shortfall: 'Shortfall',
  orderqty: 'Order Qty'
};
const BEST_COLUMN_LABELS = {
  plan: 'Plan',
  client: 'Client',
  sku: 'SKU / Description',
  sold: 'Sold',
  avg: 'Avg / Day',
  onhand: 'On Hand',
  orderqty: 'Order Qty'
};
const DELETABLE_PO_STATUSES = new Set([1, 2]);
const DEFAULT_PO_SORT_KEY = 'createdAt';
const DEFAULT_PO_SORT_DIR = 'desc';
const PO_SORTABLE_COLS = new Set([
  'poNumber',
  'client',
  'supplier',
  'warehouse',
  'notes',
  'lineCount',
  'totalQty',
  'totalReceived',
  'status',
  'expectedDate',
  'updatedAt'
]);
const VALID_PO_SORT_KEYS = new Set([...PO_SORTABLE_COLS, DEFAULT_PO_SORT_KEY]);

const BASE64_IMG_RE = /^[A-Za-z0-9+/=_-]+$/;
const normalizeItemImageValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return '';
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('//')) {
      try {
        if (typeof window !== 'undefined' && window?.location?.protocol) {
          return `${window.location.protocol}${s}`;
        }
      } catch {}
      return `https:${s}`;
    }
    if (s.startsWith('/')) return s;
    if (/^[\w./-]+\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(s)) {
      return s.startsWith('/') ? s : `/${s}`;
    }
    const compact = s.replace(/\s+/g, '');
    if (compact.length > 32 && BASE64_IMG_RE.test(compact)) {
      return `data:image/jpeg;base64,${compact}`;
    }
    return '';
  }
  if (typeof window !== 'undefined') {
    try {
      const raw = Array.isArray(value?.data)
        ? value.data
        : Array.isArray(value)
          ? value
          : value instanceof ArrayBuffer
            ? Array.from(new Uint8Array(value))
            : value?.type === 'Buffer' && Array.isArray(value?.data)
              ? value.data
              : null;
      if (raw && raw.length && typeof Blob !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        const blob = new Blob([new Uint8Array(raw)], { type: 'image/jpeg' });
        return URL.createObjectURL(blob);
      }
    } catch {}
  }
  return '';
};

const getLowStockImageSrc = (item) => {
  const fields = ['imageUrl', 'thumbnail', 'thumb', 'image1'];
  for (const key of fields) {
    const src = normalizeItemImageValue(item?.[key]);
    if (src) return src;
  }
  return '';
};

const LowStockSkuCell = ({ item }) => {
  const imageSrc = getLowStockImageSrc(item);
  const hasImage = Boolean(imageSrc);
  const skuLabel = item?.sku || '(Unknown SKU)';
  const thumbSrc = hasImage ? imageSrc : NO_IMAGE_PLACEHOLDER;
  const thumbAlt = hasImage
    ? `${skuLabel} thumbnail`
    : `${skuLabel} has no image assigned`;

  return (
    <div className="po-sku-cell">
      <div className={`po-sku-thumb ${hasImage ? '' : 'placeholder'}`}>
        <img src={thumbSrc} alt={thumbAlt} loading="lazy" decoding="async" />
      </div>
      <div className="po-sku-text">
        <span className="po-sku">{skuLabel}</span>
        {item?.description && <span className="po-sku-desc">{item.description}</span>}
      </div>
    </div>
  );
};

export default function PurchaseOrders() {
  const [lowStock, setLowStock] = useState([]);
  const [lowLoading, setLowLoading] = useState(false);
  const [lowError, setLowError] = useState('');
  const [lowInventoryTab, setLowInventoryTab] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_LOW_INVENTORY_TAB_KEY);
      if (raw === 'best') return 'best';
    } catch {}
    return 'low';
  });
  const [lowSearch, setLowSearch] = useState('');
  const [bestSellers, setBestSellers] = useState([]);
  const [bestLoading, setBestLoading] = useState(false);
  const [bestError, setBestError] = useState('');
  const [forecastEnabled, setForecastEnabled] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_FORECAST_ENABLED_KEY);
      if (raw != null) return raw === '1' || raw === 'true';
    } catch {}
    return true;
  });
  const [forecastLookbackDays, setForecastLookbackDays] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_FORECAST_LOOKBACK_KEY);
      if (raw != null) return Math.max(1, Number(raw) || 30);
    } catch {}
    return 30;
  });
  const [forecastReviewDays, setForecastReviewDays] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_FORECAST_REVIEW_KEY);
      if (raw != null) return Math.max(0, Number(raw) || 7);
    } catch {}
    return 7;
  });
  const [forecastSafetyDays, setForecastSafetyDays] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_FORECAST_SAFETY_KEY);
      if (raw != null) return Math.max(0, Number(raw) || 3);
    } catch {}
    return 3;
  });
  const [forecastByKey, setForecastByKey] = useState({});
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState('');
  const forecastAbortRef = useRef(null);
  const [forecastHelpTopic, setForecastHelpTopic] = useState('');
  const [threshold, setThreshold] = useState(() => {
    try {
      const raw = localStorage.getItem('purchasePlan.threshold');
      if (raw != null) return Number(raw) || DEFAULT_THRESHOLD;
    } catch {}
    return DEFAULT_THRESHOLD;
  });
  const [limit, setLimit] = useState(() => {
    try {
      const raw = localStorage.getItem('purchasePlan.limit');
      if (raw != null) return Number(raw) || DEFAULT_LOW_LIMIT;
    } catch {}
    return DEFAULT_LOW_LIMIT;
  });
  const [clientFilter, setClientFilter] = useState(() => {
    try {
      return localStorage.getItem(LS_PO_CLIENT_FILTER_KEY) || '';
    } catch {}
    return '';
  });
  const [supplierFilter, setSupplierFilter] = useState('');

  const [clients, setClients] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [planItems, setPlanItems] = useState([]);
  const [pendingPlanQty, setPendingPlanQty] = useState({});
  const [planClientId, setPlanClientId] = useState('');
  const [planWarehouseId, setPlanWarehouseId] = useState('');
  const [planSupplierId, setPlanSupplierId] = useState('');
  const [planExpectedDate, setPlanExpectedDate] = useState('');
  const [planNotes, setPlanNotes] = useState('');
  const [planError, setPlanError] = useState('');

  const [poList, setPoList] = useState([]);
  const [poMeta, setPoMeta] = useState({ total: 0, limit: 25, offset: 0 });
  const [poLoading, setPoLoading] = useState(false);
  const [poError, setPoError] = useState('');
  const [poStatusFilter, setPoStatusFilter] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const [poClientFilter, setPoClientFilter] = useState('');
  const [poSupplierFilter, setPoSupplierFilter] = useState('');
  const [poWarehouseFilter, setPoWarehouseFilter] = useState('');
  const [poSortKey, setPoSortKey] = useState(DEFAULT_PO_SORT_KEY);
  const [poSortDir, setPoSortDir] = useState(DEFAULT_PO_SORT_DIR);
  const [poUpdatedAt, setPoUpdatedAt] = useState('');
  const [poSaving, setPoSaving] = useState({});
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [convertModalPo, setConvertModalPo] = useState(null);
  const [convertModalLines, setConvertModalLines] = useState([]);
  const [convertModalLoading, setConvertModalLoading] = useState(false);
  const [convertModalError, setConvertModalError] = useState('');
  const [convertModalSaving, setConvertModalSaving] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetPo, setDeleteTargetPo] = useState(null);
  const [deleteModalError, setDeleteModalError] = useState('');
  const [deleteModalLoading, setDeleteModalLoading] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTargetPo, setEmailTargetPo] = useState(null);
  const [poEmailSending, setPoEmailSending] = useState(false);
  const [poEmailFields, setPoEmailFields] = useState({ to: '', cc: '', bcc: '', subject: '', message: '', includeCsv: true });
  const extractClientId = (client) =>
    client?.id ?? client?.clientid ?? client?.clientId ?? client?.client_id ?? client?.clientID ?? null;

  const poHeaderRowRef = useRef(null);
  const poTableRef = useRef(null);
  const poResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  const lowHeaderRowRef = useRef(null);
  const lowTableRef = useRef(null);
  const lowResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  const bestHeaderRowRef = useRef(null);
  const bestTableRef = useRef(null);
  const bestResizeRef = useRef({ key: null, startX: 0, startW: 0 });

  const [poColWidths, setPoColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [lowColWidths, setLowColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_LOW_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [bestColWidths, setBestColWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_BEST_COLWIDTHS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });

  const [planSplitSizes, setPlanSplitSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_SPLIT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2) return parsed;
    } catch {}
    return [68, 32];
  });

  const [verticalSplitSizes, setVerticalSplitSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_PO_V_SPLIT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2) return parsed;
    } catch {}
    return [58, 42];
  });

  const clientOptions = useMemo(() => {
    return clients
      .map((client) => {
        const idRaw = extractClientId(client);
        if (!idRaw) return null;
        return {
          id: String(idRaw),
          name: client.name || `Client #${idRaw}`
        };
      })
      .filter(Boolean);
  }, [clients]);
  const hasSingleClient = clientOptions.length === 1;
  const soleClientId = hasSingleClient ? clientOptions[0].id : '';
  const showClientControls = !hasSingleClient;
  const activePoColumns = useMemo(() => (
    showClientControls ? PO_TABLE_COLS : PO_TABLE_COLS.filter((col) => col !== 'client')
  ), [showClientControls]);
  const lowActiveColumns = useMemo(() => (
    showClientControls ? LOW_TABLE_COLUMNS : LOW_TABLE_COLUMNS.filter((col) => col !== 'client')
  ), [showClientControls]);
  const bestActiveColumns = useMemo(() => (
    showClientControls ? BEST_TABLE_COLUMNS : BEST_TABLE_COLUMNS.filter((col) => col !== 'client')
  ), [showClientControls]);
  const effectiveClientFilter = useMemo(
    () => (showClientControls ? clientFilter : soleClientId),
    [showClientControls, clientFilter, soleClientId]
  );

  useEffect(() => {
    if (hasSingleClient && soleClientId) {
      if (clientFilter !== soleClientId) setClientFilter(soleClientId);
      if (poClientFilter !== soleClientId) setPoClientFilter(soleClientId);
    }
  }, [hasSingleClient, soleClientId, clientFilter, poClientFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_PO_COLWIDTHS_KEY, JSON.stringify(poColWidths));
    } catch {}
  }, [poColWidths]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_LOW_COLWIDTHS_KEY, JSON.stringify(lowColWidths));
    } catch {}
  }, [lowColWidths]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_BEST_COLWIDTHS_KEY, JSON.stringify(bestColWidths));
    } catch {}
  }, [bestColWidths]);

  const ensurePoWidthsLocked = () => {
    if (!poHeaderRowRef.current) return;
    const widths = poColWidths || {};
    const missing = activePoColumns.some((key) => widths[key] == null);
    if (!missing) return;
    const ths = Array.from(poHeaderRowRef.current.children || []);
    const next = {};
    activePoColumns.forEach((key, idx) => {
      const th = ths[idx];
      if (th) next[key] = th.offsetWidth;
    });
    setPoColWidths((prev) => ({ ...prev, ...next }));
  };
  const ensureLowWidthsLocked = () => {
    if (!lowHeaderRowRef.current) return;
    const widths = lowColWidths || {};
    const missing = lowActiveColumns.some((key) => widths[key] == null);
    if (!missing) return;
    const ths = Array.from(lowHeaderRowRef.current.children || []);
    const next = {};
    lowActiveColumns.forEach((key, idx) => {
      const th = ths[idx];
      if (th) next[key] = th.offsetWidth;
    });
    setLowColWidths((prev) => ({ ...prev, ...next }));
  };
  const ensureBestWidthsLocked = () => {
    if (!bestHeaderRowRef.current) return;
    const widths = bestColWidths || {};
    const missing = bestActiveColumns.some((key) => widths[key] == null);
    if (!missing) return;
    const ths = Array.from(bestHeaderRowRef.current.children || []);
    const next = {};
    bestActiveColumns.forEach((key, idx) => {
      const th = ths[idx];
      if (th) next[key] = th.offsetWidth;
    });
    setBestColWidths((prev) => ({ ...prev, ...next }));
  };

  const onPoResizeMove = (e) => {
    const { key, startX, startW } = poResizeRef.current;
    if (!key) return;
    const next = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setPoColWidths((prev) => ({ ...prev, [key]: next }));
    e.preventDefault();
  };

  const onPoResizeUp = () => {
    poResizeRef.current = { key: null, startX: 0, startW: 0 };
    try {
      document.body.classList.remove('col-resizing');
    } catch {}
    window.removeEventListener('mousemove', onPoResizeMove);
    window.removeEventListener('mouseup', onPoResizeUp);
  };

  const startPoResize = (key, e) => {
    ensurePoWidthsLocked();
    const th = e.currentTarget.parentElement;
    poResizeRef.current = { key, startX: e.clientX, startW: th?.offsetWidth || MIN_COL_W };
    try {
      document.body.classList.add('col-resizing');
    } catch {}
    window.addEventListener('mousemove', onPoResizeMove);
    window.addEventListener('mouseup', onPoResizeUp);
    e.preventDefault();
    e.stopPropagation();
  };
  const onLowResizeMove = (e) => {
    const { key, startX, startW } = lowResizeRef.current;
    if (!key) return;
    const next = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setLowColWidths((prev) => ({ ...prev, [key]: next }));
    e.preventDefault();
  };

  const onLowResizeUp = () => {
    lowResizeRef.current = { key: null, startX: 0, startW: 0 };
    try {
      document.body.classList.remove('col-resizing');
    } catch {}
    window.removeEventListener('mousemove', onLowResizeMove);
    window.removeEventListener('mouseup', onLowResizeUp);
  };

  const startLowResize = (key, e) => {
    ensureLowWidthsLocked();
    const th = e.currentTarget.parentElement;
    lowResizeRef.current = { key, startX: e.clientX, startW: th?.offsetWidth || MIN_COL_W };
    try {
      document.body.classList.add('col-resizing');
    } catch {}
    window.addEventListener('mousemove', onLowResizeMove);
    window.addEventListener('mouseup', onLowResizeUp);
    e.preventDefault();
    e.stopPropagation();
  };

  const onBestResizeMove = (e) => {
    const { key, startX, startW } = bestResizeRef.current;
    if (!key) return;
    const next = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setBestColWidths((prev) => ({ ...prev, [key]: next }));
    e.preventDefault();
  };

  const onBestResizeUp = () => {
    bestResizeRef.current = { key: null, startX: 0, startW: 0 };
    try {
      document.body.classList.remove('col-resizing');
    } catch {}
    window.removeEventListener('mousemove', onBestResizeMove);
    window.removeEventListener('mouseup', onBestResizeUp);
  };

  const startBestResize = (key, e) => {
    ensureBestWidthsLocked();
    const th = e.currentTarget.parentElement;
    bestResizeRef.current = { key, startX: e.clientX, startW: th?.offsetWidth || MIN_COL_W };
    try {
      document.body.classList.add('col-resizing');
    } catch {}
    window.addEventListener('mousemove', onBestResizeMove);
    window.addEventListener('mouseup', onBestResizeUp);
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onPoResizeMove);
      window.removeEventListener('mouseup', onPoResizeUp);
    };
  }, []);
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onLowResizeMove);
      window.removeEventListener('mouseup', onLowResizeUp);
    };
  }, []);
  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onBestResizeMove);
      window.removeEventListener('mouseup', onBestResizeUp);
    };
  }, []);

  const autoFitPoCol = (key) => {
    const idx = activePoColumns.indexOf(key);
    if (idx === -1) return;
    let maxWidth = MIN_COL_W;
    const header = poHeaderRowRef.current?.children?.[idx];
    if (header) {
      maxWidth = Math.max(maxWidth, header.scrollWidth + 16);
    }
    if (poTableRef.current) {
      const rows = poTableRef.current.querySelectorAll('tbody tr');
      rows.forEach((row) => {
        const cell = row.children[idx];
        if (cell) {
          maxWidth = Math.max(maxWidth, cell.scrollWidth + 24);
        }
      });
    }
    setPoColWidths((prev) => ({ ...prev, [key]: maxWidth }));
  };
  const autoFitLowCol = (key) => {
    const idx = lowActiveColumns.indexOf(key);
    if (idx === -1) return;
    let maxWidth = MIN_COL_W;
    const header = lowHeaderRowRef.current?.children?.[idx];
    if (header) {
      maxWidth = Math.max(maxWidth, header.scrollWidth + 16);
    }
    if (lowTableRef.current) {
      const rows = lowTableRef.current.querySelectorAll('tbody tr');
      rows.forEach((row) => {
        const cell = row.children[idx];
        if (cell) {
          maxWidth = Math.max(maxWidth, cell.scrollWidth + 24);
        }
      });
    }
    setLowColWidths((prev) => ({ ...prev, [key]: maxWidth }));
  };
  const autoFitBestCol = (key) => {
    const idx = bestActiveColumns.indexOf(key);
    if (idx === -1) return;
    let maxWidth = MIN_COL_W;
    const header = bestHeaderRowRef.current?.children?.[idx];
    if (header) {
      maxWidth = Math.max(maxWidth, header.scrollWidth + 16);
    }
    if (bestTableRef.current) {
      const rows = bestTableRef.current.querySelectorAll('tbody tr');
      rows.forEach((row) => {
        const cell = row.children[idx];
        if (cell) {
          maxWidth = Math.max(maxWidth, cell.scrollWidth + 24);
        }
      });
    }
    setBestColWidths((prev) => ({ ...prev, [key]: maxWidth }));
  };

  const resetPoColumns = () => {
    setPoColWidths({});
    try {
      localStorage.removeItem(LS_PO_COLWIDTHS_KEY);
    } catch {}
    setTimeout(() => {
      ensurePoWidthsLocked();
    }, 0);
    showToast({ title: 'Columns reset', message: 'Purchase order columns restored to default widths.', variant: 'info' });
  };
  const resetLowColumns = () => {
    setLowColWidths({});
    try {
      localStorage.removeItem(LS_LOW_COLWIDTHS_KEY);
    } catch {}
    setTimeout(() => {
      ensureLowWidthsLocked();
    }, 0);
    showToast({ title: 'Columns reset', message: 'Low inventory columns restored to default widths.', variant: 'info' });
  };
  const resetBestColumns = () => {
    setBestColWidths({});
    try {
      localStorage.removeItem(LS_BEST_COLWIDTHS_KEY);
    } catch {}
    setTimeout(() => {
      ensureBestWidthsLocked();
    }, 0);
    showToast({ title: 'Columns reset', message: 'Best seller columns restored to default widths.', variant: 'info' });
  };

  useEffect(() => {
    ensurePoWidthsLocked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePoColumns]);
  useEffect(() => {
    ensureLowWidthsLocked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowActiveColumns]);
  useEffect(() => {
    ensureBestWidthsLocked();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestActiveColumns]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Purchase Orders', subtitle: 'Low Inventory Planner' }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, []);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [clientRows, warehouseRows, supplierRows] = await Promise.all([
          fetchJSON(`${API}/api/clients/minimal`).catch(() => []),
          fetchJSON(`${API}/api/warehouses`).catch(() => []),
          fetchJSON(`${API}/api/suppliers?limit=500&active=active`).catch(() => ({ data: [] }))
        ]);
        setClients(Array.isArray(clientRows) ? clientRows : []);
        const whList = Array.isArray(warehouseRows?.data) ? warehouseRows.data : Array.isArray(warehouseRows) ? warehouseRows : [];
        setWarehouses(whList);
        const supList = Array.isArray(supplierRows?.data) ? supplierRows.data : Array.isArray(supplierRows) ? supplierRows : [];
        setSuppliers(supList);
        if (!planWarehouseId && whList.length) {
          setPlanWarehouseId(String(whList[0].id ?? whList[0].warehouseid ?? ''));
        }
      } catch (err) {
        console.error('Failed to load lookups', err);
      }
    };
    loadLookups();
  }, [planWarehouseId]);

  useEffect(() => {
    try {
      if (clientFilter) localStorage.setItem(LS_PO_CLIENT_FILTER_KEY, clientFilter);
      else localStorage.removeItem(LS_PO_CLIENT_FILTER_KEY);
    } catch {}
  }, [clientFilter]);

  useEffect(() => {
    if (!clients.length) return;
    if (!clientFilter) return;
    const normalizedCurrent = String(clientFilter);
    const hasMatch = clients.some((client) => String(extractClientId(client)) === normalizedCurrent);
    if (hasMatch) return;
    // Stored client no longer exists; fall back to "All clients"
    setClientFilter('');
  }, [clients, clientFilter]);

  useEffect(() => {
    setSupplierFilter('');
  }, [clientFilter]);

  const fetchLowStock = async () => {
    setLowLoading(true);
    setLowError('');
    try {
      const params = new URLSearchParams({
        threshold: String(Math.max(0, threshold || DEFAULT_THRESHOLD)),
        limit: String(Math.max(1, limit || DEFAULT_LOW_LIMIT))
      });
      const normalizedClientId = effectiveClientFilter ? String(effectiveClientFilter).trim() : '';
      if (normalizedClientId) params.set('clientid', normalizedClientId);
      if (supplierFilter) params.set('supplierid', String(supplierFilter));
      const data = await fetchJSON(`${API}/api/inventory/low-stock?${params.toString()}`);
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setLowStock(list);
      try {
        localStorage.setItem('purchasePlan.threshold', String(threshold || DEFAULT_THRESHOLD));
        localStorage.setItem('purchasePlan.limit', String(limit || DEFAULT_LOW_LIMIT));
      } catch {}
    } catch (err) {
      setLowError(err.message || 'Unable to load low inventory');
      setLowStock([]);
    } finally {
      setLowLoading(false);
    }
  };

  useEffect(() => {
    fetchLowStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveClientFilter, supplierFilter, threshold, limit]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_PO_LOW_INVENTORY_TAB_KEY, lowInventoryTab === 'best' ? 'best' : 'low');
      localStorage.setItem(LS_PO_FORECAST_ENABLED_KEY, forecastEnabled ? '1' : '0');
      localStorage.setItem(LS_PO_FORECAST_LOOKBACK_KEY, String(Math.max(1, Number(forecastLookbackDays) || 30)));
      localStorage.setItem(LS_PO_FORECAST_REVIEW_KEY, String(Math.max(0, Number(forecastReviewDays) || 0)));
      localStorage.setItem(LS_PO_FORECAST_SAFETY_KEY, String(Math.max(0, Number(forecastSafetyDays) || 0)));
    } catch {}
  }, [lowInventoryTab, forecastEnabled, forecastLookbackDays, forecastReviewDays, forecastSafetyDays]);

  const fetchBestSellers = async () => {
    setBestLoading(true);
    setBestError('');
    try {
      const params = new URLSearchParams({
        lookbackDays: String(Math.max(1, Number(forecastLookbackDays) || 30)),
        limit: String(Math.max(1, limit || DEFAULT_LOW_LIMIT)),
        threshold: String(Math.max(0, threshold || DEFAULT_THRESHOLD)),
      });
      const normalizedClientId = effectiveClientFilter ? String(effectiveClientFilter).trim() : '';
      if (normalizedClientId) params.set('clientid', normalizedClientId);
      if (supplierFilter) params.set('supplierid', String(supplierFilter));
      const data = await fetchJSON(`${API}/api/inventory/best-sellers?${params.toString()}`);
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setBestSellers(list);
    } catch (err) {
      setBestError(err.message || 'Unable to load best sellers');
      setBestSellers([]);
    } finally {
      setBestLoading(false);
    }
  };

  useEffect(() => {
    if (lowInventoryTab !== 'best') return;
    const t = setTimeout(() => fetchBestSellers(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowInventoryTab, effectiveClientFilter, supplierFilter, forecastLookbackDays, limit, threshold]);

  const fetchForecastSuggestions = async (itemsList = lowStock) => {
    if (!forecastEnabled) {
      setForecastByKey({});
      setForecastError('');
      return;
    }
    const rows = Array.isArray(itemsList) ? itemsList : [];
    if (!rows.length) {
      setForecastByKey({});
      setForecastError('');
      return;
    }
    const payload = {
      supplierId: planSupplierId ? Number(planSupplierId) : null,
      lookbackDays: Math.max(1, Number(forecastLookbackDays) || 30),
      reviewDays: Math.max(0, Number(forecastReviewDays) || 0),
      safetyDays: Math.max(0, Number(forecastSafetyDays) || 0),
      items: rows
        .map((it) => ({ itemId: it?.itemId, clientId: it?.clientId }))
        .filter((it) => Number(it.itemId) > 0 && Number(it.clientId) > 0),
    };

    try {
      forecastAbortRef.current?.abort?.();
    } catch {}
    const controller = new AbortController();
    forecastAbortRef.current = controller;

    setForecastLoading(true);
    setForecastError('');
    try {
      const res = await fetchJSON(`${API}/api/purchase-orders/qty-suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const list = Array.isArray(res?.suggestions) ? res.suggestions : [];
      const next = {};
      for (const s of list) {
        const itemId = Number(s?.itemId || 0);
        const clientId = Number(s?.clientId || 0);
        if (!itemId || !clientId) continue;
        next[`${clientId}:${itemId}`] = s;
      }
      setForecastByKey(next);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setForecastError(err?.message || 'Failed to load forecast suggestions');
      setForecastByKey({});
    } finally {
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const src = lowInventoryTab === 'best' ? bestSellers : lowStock;
      fetchForecastSuggestions(src);
    }, 350);
    return () => {
      clearTimeout(t);
      try { forecastAbortRef.current?.abort?.(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastEnabled, forecastLookbackDays, forecastReviewDays, forecastSafetyDays, planSupplierId, lowInventoryTab, lowStock, bestSellers]);

  const fetchPurchaseOrders = async (offset = 0) => {
    setPoLoading(true);
    setPoError('');
    try {
      const limitValue = poMeta.limit || 25;
      const params = new URLSearchParams({
        limit: String(limitValue),
        offset: String(offset)
      });
      if (poStatusFilter) params.set('status', poStatusFilter);
      if (poClientFilter) params.set('clientId', poClientFilter);
      if (poSupplierFilter) params.set('supplierId', poSupplierFilter);
      if (poWarehouseFilter) params.set('warehouseId', poWarehouseFilter);
      if (poSearch.trim()) params.set('q', poSearch.trim());
      if (poSortKey) params.set('sort', poSortKey);
      if (poSortDir) params.set('dir', poSortDir);
      const res = await fetchJSON(`${API}/api/purchase-orders?${params.toString()}`);
      setPoList(Array.isArray(res?.data) ? res.data : []);
      const meta = res?.meta || {};
      setPoMeta((prev) => ({
        total: Number(meta.total ?? 0),
        limit: Number(meta.limit ?? prev.limit ?? 25),
        offset: Number(meta.offset ?? offset)
      }));
      if (meta.sort && VALID_PO_SORT_KEYS.has(meta.sort) && meta.sort !== poSortKey) {
        setPoSortKey(meta.sort);
      }
      const metaDir = typeof meta.dir === 'string' ? meta.dir.toLowerCase() : '';
      if ((metaDir === 'asc' || metaDir === 'desc') && metaDir !== poSortDir) {
        setPoSortDir(metaDir);
      }
      setPoUpdatedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setPoError(err.message || 'Unable to load purchase orders');
      setPoList([]);
    } finally {
      setPoLoading(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchPurchaseOrders(0);
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poStatusFilter, poSearch, poClientFilter, poSupplierFilter, poWarehouseFilter, poSortKey, poSortDir]);

  const fetchPurchaseOrderDetail = async (poId) => {
    if (!poId) throw new Error('Invalid purchase order id');
    return fetchJSON(`${API}/api/purchase-orders/${poId}`);
  };

  const handlePoSort = (columnKey) => {
    if (!PO_SORTABLE_COLS.has(columnKey)) return;
    setPoMeta((prev) => ({ ...prev, offset: 0 }));
    setPoSortKey((prevKey) => {
      if (prevKey === columnKey) {
        setPoSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setPoSortDir('asc');
      return columnKey;
    });
  };

  const canDeletePo = (po) => !!po && DELETABLE_PO_STATUSES.has(Number(po.status));

  const openDeleteModal = (po) => {
    if (!canDeletePo(po)) return;
    setDeleteTargetPo(po);
    setDeleteModalError('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteModalLoading) return;
    setDeleteModalOpen(false);
    setDeleteTargetPo(null);
    setDeleteModalError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetPo?.id) return;
    setDeleteModalLoading(true);
    setDeleteModalError('');
    try {
      await fetchJSON(`${API}/api/purchase-orders/${deleteTargetPo.id}`, {
        method: 'DELETE'
      });
      showToast({
        title: 'Purchase order deleted',
        message: `${formatPoLabel(deleteTargetPo)} removed.`,
        variant: 'success'
      });
      setDeleteModalOpen(false);
      setDeleteTargetPo(null);
      fetchPurchaseOrders(poMeta.offset || 0);
    } catch (err) {
      const message = err.message || 'Failed to delete purchase order';
      setDeleteModalError(message);
      showToast({ title: 'Delete failed', message, variant: 'error' });
    } finally {
      setDeleteModalLoading(false);
    }
  };

  const planItemMap = useMemo(() => {
    const map = new Map();
    for (const item of planItems) {
      map.set(item.itemId, item);
    }
    return map;
  }, [planItems]);

  const supplierLookup = useMemo(() => {
    const map = new Map();
    suppliers.forEach((supplier) => {
      const supIdRaw = supplier.id ?? supplier.supplier_id ?? supplier.supplierId;
      if (!supIdRaw) return;
      const supId = String(supIdRaw);
      const supName = supplier.name ?? supplier.displayname ?? supplier.supplier_name ?? `Supplier #${supId}`;
      map.set(supId, supName);
    });
    return map;
  }, [suppliers]);

  const filteredLowStock = useMemo(() => {
    const needle = lowSearch.trim().toLowerCase();
    const normalizedClient = effectiveClientFilter ? String(effectiveClientFilter).trim() : '';
    return lowStock
      .filter((item) => {
        if (normalizedClient) {
          const itemClientId = item?.clientId != null ? String(item.clientId) : '';
          if (itemClientId !== normalizedClient) return false;
        }
        if (supplierFilter) {
          if (String(item.supplierId ?? '') !== supplierFilter) return false;
        }
        const supplierLabel = item.supplierId != null ? (supplierLookup.get(String(item.supplierId)) || '') : '';
        if (needle) {
          const haystack = `${item.sku || ''} ${item.description || ''} ${item.clientName || ''} ${supplierLabel}`.toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const diff = shortfallForItem(b) - shortfallForItem(a);
        if (diff !== 0) return diff;
        return (b.lowThreshold ?? 0) - (a.lowThreshold ?? 0);
      });
  }, [lowStock, lowSearch, supplierFilter, supplierLookup, effectiveClientFilter]);

  const filteredBestSellers = useMemo(() => {
    const needle = lowSearch.trim().toLowerCase();
    const normalizedClient = effectiveClientFilter ? String(effectiveClientFilter).trim() : '';
    return bestSellers
      .filter((item) => {
        if (normalizedClient) {
          const itemClientId = item?.clientId != null ? String(item.clientId) : '';
          if (itemClientId !== normalizedClient) return false;
        }
        if (supplierFilter) {
          if (String(item.supplierId ?? '') !== supplierFilter) return false;
        }
        const supplierLabel = item.supplierId != null ? (supplierLookup.get(String(item.supplierId)) || '') : '';
        if (needle) {
          const haystack = `${item.sku || ''} ${item.description || ''} ${item.clientName || ''} ${supplierLabel}`.toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        return true;
      });
  }, [bestSellers, lowSearch, supplierFilter, supplierLookup, effectiveClientFilter]);

  const planTotals = useMemo(() => {
    const totalQty = planItems.reduce((sum, item) => sum + Number(item.qtyOrdered || 0), 0);
    const shortfall = planItems.reduce((sum, item) => sum + shortfallForItem(item), 0);
    return { totalQty, shortfall };
  }, [planItems]);

  const planClient = useMemo(() => {
    if (!planClientId) return null;
    return clients.find((c) => String(c.id ?? c.clientid) === String(planClientId)) || null;
  }, [clients, planClientId]);

  const normalizeQty = (value) => Math.max(1, Number(value) || 0);
  const getForecastSuggestion = (item) => {
    const key = `${item?.clientId || 0}:${item?.itemId || 0}`;
    const row = forecastByKey[key];
    const suggested = Number(row?.suggestedQty || row?.suggestedqty || 0);
    return Number.isFinite(suggested) ? Math.max(0, Math.round(suggested)) : 0;
  };
  const defaultPlanQty = (item) => {
    const hasExplicitThreshold = item?.lowThreshold != null && Number.isFinite(Number(item.lowThreshold));
    const base = hasExplicitThreshold
      ? (shortfallForItem(item) || Math.max(Number(item.lowThreshold) || DEFAULT_THRESHOLD, 1))
      : 1;
    if (!forecastEnabled) return base;
    const suggested = getForecastSuggestion(item);
    return suggested > 0 ? Math.max(base, suggested) : base;
  };

  const forecastHelpContent = useMemo(() => {
    const baseFormula = 'Suggested = ceil(avg_daily_demand × (lead_time + review + safety) − on_hand − on_order)';
    return {
      lookback: {
        title: 'Lookback (days)',
        body: [
          'How many days back we look at closed/shipped eOrders to estimate average daily demand.',
          'Bigger lookback = smoother/less reactive. Smaller lookback = more reactive/more noise.',
          baseFormula,
        ],
      },
      review: {
        title: 'Review (days)',
        body: [
          'Extra days of demand to cover beyond the supplier lead time.',
          'Think of this as the time between “placing this PO” and “your next chance to reorder”.',
          baseFormula,
        ],
      },
      safety: {
        title: 'Safety (days)',
        body: [
          'Additional buffer days added on top of lead time + review to handle spikes, delays, and variability.',
          'Higher safety = fewer stockouts, but more inventory.',
          baseFormula,
        ],
      },
    };
  }, []);

  const handleTogglePlanItem = (item) => {
    if (planItemMap.has(item.itemId)) {
      const nextItems = planItems.filter((row) => row.itemId !== item.itemId);
      setPlanItems(nextItems);
      if (nextItems.length === 0) {
        setPlanClientId('');
      }
      const planItem = planItemMap.get(item.itemId);
      setPendingPlanQty((prev) => ({ ...prev, [item.itemId]: planItem?.qtyOrdered || defaultPlanQty(item) }));
      return;
    }
    if (planClientId && String(planClientId) !== String(item.clientId)) {
      setPlanError('Plan can only contain SKUs for a single client.');
      showToast({ title: 'Cannot mix clients', message: 'Clear the current plan before adding another client.', variant: 'error' });
      return;
    }
    const pendingQty = pendingPlanQty[item.itemId];
    const qty = normalizeQty(pendingQty != null ? pendingQty : defaultPlanQty(item));
    setPlanItems((prev) => [...prev, { ...item, qtyOrdered: qty }]);
    setPlanClientId(String(item.clientId || ''));
    setPlanError('');
    setPendingPlanQty((prev) => {
      const next = { ...prev };
      delete next[item.itemId];
      return next;
    });
  };

  const handlePlanQtyChange = (itemId, value) => {
    const qty = normalizeQty(value);
    setPlanItems((prev) =>
      prev.map((item) =>
        item.itemId === itemId
          ? { ...item, qtyOrdered: qty }
          : item
      )
    );
  };

  const handleLowQtyInputChange = (item, value) => {
    const qty = normalizeQty(value);
    if (planItemMap.has(item.itemId)) {
      handlePlanQtyChange(item.itemId, qty);
    } else {
      setPendingPlanQty((prev) => ({ ...prev, [item.itemId]: qty }));
    }
  };

  const handleRemovePlanItem = (item) => {
    const itemId = item?.itemId;
    if (!itemId) return;
    const nextItems = planItems.filter((row) => row.itemId !== itemId);
    setPlanItems(nextItems);
    if (nextItems.length === 0) setPlanClientId('');
    setPlanError('');
    setPendingPlanQty((prev) => ({ ...prev, [itemId]: Number(item?.qtyOrdered || 1) }));
  };

  const getQtyInputValue = (item) => {
    const planItem = planItemMap.get(item.itemId);
    if (planItem) return planItem.qtyOrdered;
    if (pendingPlanQty[item.itemId] != null) return pendingPlanQty[item.itemId];
    return defaultPlanQty(item);
  };

  const clearPlan = () => {
    setPlanItems([]);
    setPlanClientId('');
    setPlanSupplierId('');
    setPlanExpectedDate('');
    setPlanNotes('');
    setPlanError('');
    setPendingPlanQty({});
  };

  const planSubmitDisabled = planItems.length === 0 || !planWarehouseId;

  const handleCreatePurchaseOrder = async () => {
    if (planSubmitDisabled) {
      setPlanError('Select at least one SKU and a warehouse.');
      return;
    }
    try {
      const payload = {
        clientId: Number(planClientId),
        warehouseId: Number(planWarehouseId),
        supplierId: planSupplierId ? Number(planSupplierId) : null,
        expectedDate: planExpectedDate || null,
        notes: planNotes || '',
        lines: planItems.map((item) => ({
          itemId: item.itemId,
          sku: item.sku,
          description: item.description,
          qtyOrdered: Number(item.qtyOrdered || 0),
          unitCost: item.unitCost ?? null
        }))
      };
      const created = await fetchJSON(`${API}/api/purchase-orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      showToast({
        title: 'Purchase order created',
        message: `PO ${created?.poNumber || created?.id} queued.`,
        variant: 'success'
      });
      clearPlan();
      fetchPurchaseOrders(poMeta.offset || 0);
    } catch (err) {
      setPlanError(err.message || 'Failed to create purchase order');
      showToast({ title: 'Failed to create PO', message: err.message, variant: 'error' });
    }
  };

  const markPoSaving = (id, value) => {
    setPoSaving((prev) => {
      const next = { ...prev, [id]: value };
      if (!value) delete next[id];
      return next;
    });
  };

  const formatPoLabel = (po) => {
    if (!po) return 'PO';
    return po.poNumber || `PO-${po.id}`;
  };

  const handlePrintPo = (po) => {
    if (!po?.id) return;
    try {
      const url = `${API}/api/purchase-orders/${po.id}/pdf?v=${Date.now()}`;
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) throw new Error('Pop-up blocked. Please allow pop-ups to download the PDF.');
    } catch (err) {
      showToast({ title: 'Print failed', message: err.message, variant: 'error' });
    }
  };

  const openEmailModal = (po) => {
    if (!po) return;
    const defaultSubject = `Purchase Order ${formatPoLabel(po)}`;
    const messageLines = [
      `Purchase Order: ${formatPoLabel(po)}`,
      `Client: ${po.clientName || '-'}`,
      `Supplier: ${po.supplierName || '-'}`,
      `Warehouse: ${po.warehouseName || '-'}`,
      `Expected Date: ${po.expectedDate || '-'}`,
      '',
      'Please see the attached PDF for details.',
    ];
    setEmailTargetPo(po);
    setPoEmailFields({
      to: '',
      cc: '',
      bcc: '',
      subject: defaultSubject,
      message: messageLines.join('\n'),
      includeCsv: true,
    });
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    if (poEmailSending) return;
    setEmailModalOpen(false);
    setEmailTargetPo(null);
  };

  const handleEmailPo = (po) => {
    if (!po) return;
    openEmailModal(po);
  };

  const submitEmailPo = async () => {
    if (!emailTargetPo?.id) return;
    const { to, cc, bcc, subject, message, includeCsv } = poEmailFields;
    if (!String(to || '').trim()) {
      showToast({ title: 'Missing recipient', message: 'Enter at least one recipient email.', variant: 'error' });
      return;
    }
    setPoEmailSending(true);
    try {
      await fetchJSON(`${API}/api/purchase-orders/${emailTargetPo.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, cc, bcc, subject, message, includeCsv }),
      });
      showToast({
        title: 'Email sent',
        message: `Purchase order ${formatPoLabel(emailTargetPo)} emailed successfully.`,
        variant: 'success',
      });
      closeEmailModal();
    } catch (err) {
      showToast({ title: 'Email failed', message: err.message, variant: 'error' });
    } finally {
      setPoEmailSending(false);
    }
  };

  const handleExportPoCsv = (po) => {
    if (!po?.id) return;
    try {
      const url = `${API}/api/purchase-orders/${po.id}/csv?v=${Date.now()}`;
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) throw new Error('Pop-up blocked. Please allow pop-ups to download the CSV.');
    } catch (err) {
      showToast({ title: 'Export failed', message: err.message, variant: 'error' });
    }
  };

  const mapConvertLine = (line, idx = 0) => {
    const orderedQty = Number(line?.qtyOrdered ?? line?.qty_ordered ?? 0) || 0;
    const prevReceived = Number(line?.qtyReceived ?? line?.qty_received ?? 0) || 0;
    const outstanding = Math.max(0, orderedQty - prevReceived);
    return {
      lineId: line?.id ?? idx,
      itemId: line?.itemId ?? line?.item_id ?? null,
      sku: line?.sku || `Item #${line?.itemId ?? ''}`,
      description: line?.description || '',
      orderedQty,
      prevReceived,
      outstanding,
      actualQty: outstanding > 0 ? outstanding : 0
    };
  };

  const handleOpenConvertModal = async (po) => {
    if (!po?.id) return;
    setConvertModalPo(po);
    setConvertModalLines([]);
    setConvertModalError('');
    setConvertModalOpen(true);
    setConvertModalLoading(true);
    try {
      const detail = await fetchPurchaseOrderDetail(po.id);
      const rawLines = Array.isArray(detail?.lines) ? detail.lines : [];
      setConvertModalLines(rawLines.map((line, idx) => mapConvertLine(line, idx)));
    } catch (err) {
      setConvertModalError(err.message || 'Unable to load purchase order lines');
    } finally {
      setConvertModalLoading(false);
    }
  };

  const closeConvertModal = () => {
    if (convertModalSaving) return;
    setConvertModalOpen(false);
    setConvertModalPo(null);
    setConvertModalLines([]);
    setConvertModalError('');
  };

  const handleConvertQtyChange = (lineId, value) => {
    const qty = Math.max(0, Number(value) || 0);
    setConvertModalLines((prev) =>
      prev.map((line) => {
        if (line.lineId !== lineId) return line;
        const limited = Math.min(qty, Math.max(0, line.outstanding));
        return { ...line, actualQty: limited };
      })
    );
  };

  const convertTotals = useMemo(() => {
    return convertModalLines.reduce(
      (totals, line) => {
        const actual = Number(line.actualQty) || 0;
        totals.ordered += Number(line.orderedQty) || 0;
        totals.prevReceived += Number(line.prevReceived) || 0;
        totals.outstanding += Number(line.outstanding) || 0;
        totals.actual += actual;
        return totals;
      },
      { ordered: 0, prevReceived: 0, outstanding: 0, actual: 0 }
    );
  }, [convertModalLines]);

  const convertHasQty = convertModalLines.some((line) => Number(line.actualQty) > 0);
  const convertHasShortages = convertModalLines.some(
    (line) => Number(line.outstanding) > 0 && Number(line.actualQty) < Number(line.outstanding)
  );
  const convertHasOverages = convertModalLines.some(
    (line) => Number(line.actualQty) > Number(line.outstanding)
  );

  const submitConvertModal = async () => {
    if (!convertModalPo) return;
    const payloadLines = convertModalLines
      .filter((line) => Number(line.actualQty) > 0)
      .map((line) => ({
        id: line.lineId,
        itemId: line.itemId,
        qty: Number(line.actualQty) || 0
      }));
    if (!payloadLines.length) {
      setConvertModalError('Enter at least one actual quantity to convert.');
      return;
    }
    setConvertModalSaving(true);
    setConvertModalError('');
    markPoSaving(convertModalPo.id, true);
    try {
      await fetchJSON(`${API}/api/purchase-orders/${convertModalPo.id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lines: payloadLines })
      });
      showToast({
        title: 'Receipt created',
        message: `Purchase order ${convertModalPo.poNumber || convertModalPo.id} converted.`,
        variant: 'success'
      });
      closeConvertModal();
      fetchPurchaseOrders(poMeta.offset || 0);
    } catch (err) {
      setConvertModalError(err.message || 'Conversion failed');
      showToast({ title: 'Conversion failed', message: err.message, variant: 'error' });
    } finally {
      setConvertModalSaving(false);
      markPoSaving(convertModalPo.id, false);
    }
  };

  const handleUpdateStatus = async (po, nextStatus) => {
    const statusValue = Number(nextStatus);
    markPoSaving(po.id, true);
    try {
      await fetchJSON(`${API}/api/purchase-orders/${po.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: statusValue })
      });
      setPoList((prev) =>
        prev.map((row) => (row.id === po.id ? { ...row, status: statusValue } : row))
      );
      showToast({ title: 'Status updated', message: `PO ${po.poNumber || po.id} is now ${STATUS_LABELS[statusValue]}.`, variant: 'success' });
    } catch (err) {
      showToast({ title: 'Failed to update', message: err.message, variant: 'error' });
    } finally {
      markPoSaving(po.id, false);
    }
  };

  const handleConvertPurchaseOrder = (po) => {
    handleOpenConvertModal(po);
  };

  const handlePoPageChange = (direction) => {
    const nextOffset = Math.max(0, (poMeta.offset || 0) + direction * (poMeta.limit || 25));
    if (nextOffset === poMeta.offset) return;
    fetchPurchaseOrders(nextOffset);
  };

  const lowStats = useMemo(() => {
    const totalSkus = filteredLowStock.length;
    const totalShort = filteredLowStock.reduce((sum, item) => sum + shortfallForItem(item), 0);
    return { totalSkus, totalShort };
  }, [filteredLowStock]);

  const bestStats = useMemo(() => {
    const totalSkus = filteredBestSellers.length;
    const totalUnitsSold = filteredBestSellers.reduce((sum, item) => sum + Number(item.unitsSold || 0), 0);
    return { totalSkus, totalUnitsSold };
  }, [filteredBestSellers]);

  return (
    <div className="purchase-orders-page">
      <div className="po-toolbar">
        <div className="po-search-field">
          <FaSearch />
          <input
            value={lowSearch}
            onChange={(e) => setLowSearch(e.target.value)}
            placeholder="Search SKU, description, client…"
          />
        </div>
        <label>
          <span>Threshold</span>
          <input
            type="number"
            min="0"
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value) || 0)}
          />
        </label>
        <label>
          <span>Limit</span>
          <input
            type="number"
            min="1"
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Number(e.target.value) || DEFAULT_LOW_LIMIT))}
          />
        </label>
        <label className="po-forecast-toggle" title="Suggest order quantities using closed/shipped eOrders + lead time">
          <span>Forecast</span>
          <input
            type="checkbox"
            checked={forecastEnabled}
            onChange={(e) => setForecastEnabled(e.target.checked)}
          />
        </label>
        <label title="How many days back to look at closed/shipped eOrders for demand">
          <span className="po-label-with-help">
            Lookback
            <button
              type="button"
              className="po-help-icon-btn"
              onClick={() => setForecastHelpTopic('lookback')}
              aria-label="What does Lookback mean?"
              title="Help"
            >
              <FaQuestionCircle />
            </button>
          </span>
          <input
            type="number"
            min="1"
            max="365"
            value={forecastLookbackDays}
            onChange={(e) => setForecastLookbackDays(Math.max(1, Number(e.target.value) || 1))}
            disabled={!forecastEnabled}
          />
        </label>
        <label title="Extra days of coverage beyond supplier lead time (review period)">
          <span className="po-label-with-help">
            Review
            <button
              type="button"
              className="po-help-icon-btn"
              onClick={() => setForecastHelpTopic('review')}
              aria-label="What does Review mean?"
              title="Help"
            >
              <FaQuestionCircle />
            </button>
          </span>
          <input
            type="number"
            min="0"
            max="90"
            value={forecastReviewDays}
            onChange={(e) => setForecastReviewDays(Math.max(0, Number(e.target.value) || 0))}
            disabled={!forecastEnabled}
          />
        </label>
        <label title="Safety-stock days added to lead time + review days">
          <span className="po-label-with-help">
            Safety
            <button
              type="button"
              className="po-help-icon-btn"
              onClick={() => setForecastHelpTopic('safety')}
              aria-label="What does Safety mean?"
              title="Help"
            >
              <FaQuestionCircle />
            </button>
          </span>
          <input
            type="number"
            min="0"
            max="90"
            value={forecastSafetyDays}
            onChange={(e) => setForecastSafetyDays(Math.max(0, Number(e.target.value) || 0))}
            disabled={!forecastEnabled}
          />
        </label>
        {showClientControls && (
          <label>
            <span>Client</span>
            <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
              <option value="">All clients</option>
              {clientOptions.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span>Supplier</span>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
          >
            <option value="">All suppliers</option>
            {suppliers.map((supplier) => {
              const supIdRaw = supplier.id ?? supplier.supplier_id ?? supplier.supplierId;
              if (!supIdRaw) return null;
              const supId = String(supIdRaw);
              const supName = supplier.name ?? supplier.displayname ?? supplier.supplier_name ?? `Supplier #${supId}`;
              return (
                <option key={supId} value={supId}>
                  {supName}
                </option>
              );
            })}
          </select>
        </label>
        <button
          className="btn"
          onClick={lowInventoryTab === 'best' ? fetchBestSellers : fetchLowStock}
          disabled={lowInventoryTab === 'best' ? bestLoading : lowLoading}
        >
          <FaSync /> Refresh
        </button>
      </div>

      <div className="po-layout">
        <Split
          className="po-page-split"
          direction="vertical"
          gutterSize={10}
          minSize={[360, 240]}
          sizes={verticalSplitSizes}
          style={{ width: '100%', height: '100%' }}
          onDragEnd={(sizes) => {
            setVerticalSplitSizes(sizes);
            try { localStorage.setItem(LS_PO_V_SPLIT_KEY, JSON.stringify(sizes)); } catch {}
          }}
        >
          <div className="po-top-pane">
            {lowInventoryTab === 'best'
              ? (bestError && <div className="po-error">{bestError}</div>)
              : (lowError && <div className="po-error">{lowError}</div>)
            }
            {forecastEnabled && forecastError && (
              <div className="po-error">
                Forecast: {forecastError}
              </div>
            )}
            <Split
              className="po-top-split"
              direction="horizontal"
              gutterSize={10}
              minSize={[360, 260]}
              sizes={planSplitSizes}
              style={{ width: '100%', height: '100%' }}
              onDragEnd={(sizes) => {
                setPlanSplitSizes(sizes);
                try { localStorage.setItem(LS_PO_SPLIT_KEY, JSON.stringify(sizes)); } catch {}
              }}
            >
              <div className="po-pane">
                <section className="po-table-card">
                  <div className="po-table-header">
                    <div>
                      <h2>
                        {lowInventoryTab === 'best'
                          ? `Best Sellers (${bestStats.totalSkus})`
                          : `Low Inventory (${lowStats.totalSkus})`}
                      </h2>
                      <div className="po-table-stats">
                        {lowInventoryTab === 'best' ? (
                          <span>
                            <span className="po-stat-label">Units sold ({formatNumber(forecastLookbackDays)}d)</span>
                            {formatNumber(bestStats.totalUnitsSold, { maximumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span>
                            <span className="po-stat-label">Total shortfall</span>
                            {formatNumber(lowStats.totalShort)}
                          </span>
                        )}
                      </div>
                      <div className="po-inventory-tabs" role="tablist" aria-label="Low inventory tabs">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={lowInventoryTab !== 'best'}
                          className={`po-tab-btn ${lowInventoryTab !== 'best' ? 'active' : ''}`}
                          onClick={() => setLowInventoryTab('low')}
                        >
                          Low Stock
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={lowInventoryTab === 'best'}
                          className={`po-tab-btn ${lowInventoryTab === 'best' ? 'active' : ''}`}
                          onClick={() => setLowInventoryTab('best')}
                        >
                          Best Sellers
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div className="po-updated">
                        {lowInventoryTab === 'best'
                          ? (bestLoading ? 'Loading…' : 'Live view')
                          : (lowLoading ? 'Loading…' : 'Live view')}
                      </div>
                      <button
                        className="btn secondary"
                        onClick={lowInventoryTab === 'best' ? resetBestColumns : resetLowColumns}
                      >
                        <FaRedo style={{ marginRight: 6 }} /> Reset Columns
                      </button>
                    </div>
                  </div>
                  <div className="po-table-scroll">
                    {lowInventoryTab === 'best' ? (
                      bestLoading ? (
                        <div className="loading">Loading best sellers…</div>
                      ) : (
                        <table className="po-table po-best-table" ref={bestTableRef}>
                          <colgroup>
                            {bestActiveColumns.map((col, idx) => {
                              const isLast = idx === bestActiveColumns.length - 1;
                              const width = bestColWidths[col];
                              return (
                                <col
                                  key={col}
                                  style={!isLast && width ? { width: `${width}px` } : undefined}
                                />
                              );
                            })}
                          </colgroup>
                          <thead>
                            <tr ref={bestHeaderRowRef}>
                              <th className="po-select-col" data-colkey="plan">
                                <span className="po-header-label">{BEST_COLUMN_LABELS.plan}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('plan', e)}
                                  onDoubleClick={() => autoFitBestCol('plan')}
                                />
                              </th>
                              {showClientControls && (
                                <th data-colkey="client">
                                  <span className="po-header-label">{BEST_COLUMN_LABELS.client}</span>
                                  <span
                                    className="col-resizer"
                                    onMouseDown={(e) => startBestResize('client', e)}
                                    onDoubleClick={() => autoFitBestCol('client')}
                                  />
                                </th>
                              )}
                              <th data-colkey="sku">
                                <span className="po-header-label">{BEST_COLUMN_LABELS.sku}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('sku', e)}
                                  onDoubleClick={() => autoFitBestCol('sku')}
                                />
                              </th>
                              <th data-colkey="sold">
                                <span className="po-header-label">
                                  {BEST_COLUMN_LABELS.sold} ({formatNumber(forecastLookbackDays)}d)
                                </span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('sold', e)}
                                  onDoubleClick={() => autoFitBestCol('sold')}
                                />
                              </th>
                              <th data-colkey="avg">
                                <span className="po-header-label">{BEST_COLUMN_LABELS.avg}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('avg', e)}
                                  onDoubleClick={() => autoFitBestCol('avg')}
                                />
                              </th>
                              <th data-colkey="onhand">
                                <span className="po-header-label">{BEST_COLUMN_LABELS.onhand}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('onhand', e)}
                                  onDoubleClick={() => autoFitBestCol('onhand')}
                                />
                              </th>
                              <th data-colkey="orderqty">
                                <span className="po-header-label">{BEST_COLUMN_LABELS.orderqty}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startBestResize('orderqty', e)}
                                  onDoubleClick={() => autoFitBestCol('orderqty')}
                                />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBestSellers.length === 0 ? (
                              <tr>
                                <td colSpan={bestActiveColumns.length} className="po-empty">
                                  No best sellers found for the selected filters.
                                </td>
                              </tr>
                            ) : (
                              filteredBestSellers.map((item) => {
                                const selected = planItemMap.has(item.itemId);
                                return (
                                  <tr key={`${item.clientId}-${item.itemId}`} className={selected ? 'active' : ''}>
                                    <td className="po-select-cell" data-colkey="plan">
                                      <button
                                        className="mini-btn"
                                        onClick={() => handleTogglePlanItem(item)}
                                        title={selected ? 'Remove from plan' : 'Add to plan'}
                                      >
                                        {selected ? <FaTrash /> : <FaPlus />}
                                      </button>
                                    </td>
                                    {showClientControls && (
                                      <td data-colkey="client">
                                        <div className="po-client-cell">
                                          <div>
                                            <div className="po-client-name">{item.clientName}</div>
                                            <span className="po-muted">ID #{item.clientId}</span>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    <td data-colkey="sku">
                                      <LowStockSkuCell item={item} />
                                    </td>
                                    <td data-colkey="sold">{formatNumber(item.unitsSold, { maximumFractionDigits: 2 })}</td>
                                    <td data-colkey="avg">{formatNumber(item.avgDailyUnits, { maximumFractionDigits: 3 })}</td>
                                    <td data-colkey="onhand">{formatNumber(item.qtyUnitsOnHand)}</td>
                                    <td data-colkey="orderqty">
                                      <div className="po-qty-editor">
                                        <input
                                          type="number"
                                          min="1"
                                          value={getQtyInputValue(item)}
                                          onChange={(e) => handleLowQtyInputChange(item, e.target.value)}
                                        />
                                        {forecastEnabled && (() => {
                                          const suggested = getForecastSuggestion(item);
                                          if (!forecastLoading && suggested <= 0) return null;
                                          const title = (() => {
                                            const k = `${item?.clientId || 0}:${item?.itemId || 0}`;
                                            const s = forecastByKey[k];
                                            if (!s) return '';
                                            const lead = Number(s.leadTimeDays || 0);
                                            const targetDays = Number(s.targetDays || 0);
                                            const avg = Number(s.avgDailyUnits || 0);
                                            const sold = Number(s.unitsSold || 0);
                                            const onOrder = Number(s.onOrderUnits || 0);
                                            return [
                                              `Lead time: ${lead}d`,
                                              `Target coverage: ${targetDays}d`,
                                              `Avg daily demand: ${avg.toFixed(3)} units/day`,
                                              `Demand in lookback: ${sold.toFixed(2)} units (${forecastLookbackDays}d)`,
                                              `On order: ${onOrder} units`,
                                            ].join('\n');
                                          })();
                                          return (
                                            <div className="po-qty-hint" title={title}>
                                              {forecastLoading ? 'Forecasting…' : `Suggested: ${formatNumber(suggested)}`}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      )
                    ) : (
                      lowLoading ? (
                        <div className="loading">Loading low inventory…</div>
                      ) : (
                        <table className="po-table" ref={lowTableRef}>
                          <colgroup>
                            {lowActiveColumns.map((col, idx) => {
                              const isLast = idx === lowActiveColumns.length - 1;
                              const width = lowColWidths[col];
                              return (
                                <col
                                  key={col}
                                  style={!isLast && width ? { width: `${width}px` } : undefined}
                                />
                              );
                            })}
                          </colgroup>
                          <thead>
                            <tr ref={lowHeaderRowRef}>
                              <th className="po-select-col" data-colkey="plan">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.plan}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('plan', e)}
                                  onDoubleClick={() => autoFitLowCol('plan')}
                                />
                              </th>
                              {showClientControls && (
                                <th data-colkey="client">
                                  <span className="po-header-label">{LOW_COLUMN_LABELS.client}</span>
                                  <span
                                    className="col-resizer"
                                    onMouseDown={(e) => startLowResize('client', e)}
                                    onDoubleClick={() => autoFitLowCol('client')}
                                  />
                                </th>
                              )}
                              <th data-colkey="sku">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.sku}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('sku', e)}
                                  onDoubleClick={() => autoFitLowCol('sku')}
                                />
                              </th>
                              <th data-colkey="onhand">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.onhand}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('onhand', e)}
                                  onDoubleClick={() => autoFitLowCol('onhand')}
                                />
                              </th>
                              <th data-colkey="threshold">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.threshold}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('threshold', e)}
                                  onDoubleClick={() => autoFitLowCol('threshold')}
                                />
                              </th>
                              <th data-colkey="shortfall">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.shortfall}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('shortfall', e)}
                                  onDoubleClick={() => autoFitLowCol('shortfall')}
                                />
                              </th>
                              <th data-colkey="orderqty">
                                <span className="po-header-label">{LOW_COLUMN_LABELS.orderqty}</span>
                                <span
                                  className="col-resizer"
                                  onMouseDown={(e) => startLowResize('orderqty', e)}
                                  onDoubleClick={() => autoFitLowCol('orderqty')}
                                />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredLowStock.length === 0 ? (
                              <tr>
                                <td colSpan={lowActiveColumns.length} className="po-empty">
                                  No SKUs are at or below their thresholds.
                                </td>
                              </tr>
                            ) : (
                              filteredLowStock.map((item) => {
                                const selected = planItemMap.has(item.itemId);
                                const shortfall = shortfallForItem(item);
                                return (
                                  <tr key={`${item.clientId}-${item.itemId}`} className={selected ? 'active' : ''}>
                                    <td className="po-select-cell" data-colkey="plan">
                                      <button
                                        className="mini-btn"
                                        onClick={() => handleTogglePlanItem(item)}
                                        title={selected ? 'Remove from plan' : 'Add to plan'}
                                      >
                                        {selected ? <FaTrash /> : <FaPlus />}
                                      </button>
                                    </td>
                                    {showClientControls && (
                                      <td data-colkey="client">
                                        <div className="po-client-cell">
                                          <div>
                                            <div className="po-client-name">{item.clientName}</div>
                                            <span className="po-muted">ID #{item.clientId}</span>
                                          </div>
                                        </div>
                                      </td>
                                    )}
                                    <td data-colkey="sku">
                                      <LowStockSkuCell item={item} />
                                    </td>
                                    <td data-colkey="onhand">{formatNumber(item.qtyUnitsOnHand)}</td>
                                    <td data-colkey="threshold">{formatNumber(item.lowThreshold)}</td>
                                    <td data-colkey="shortfall">
                                      <span className={`po-shortfall ${shortfall > 0 ? 'low' : ''}`}>
                                        {formatNumber(shortfall)}
                                      </span>
                                    </td>
                                    <td data-colkey="orderqty">
                                      <div className="po-qty-editor">
                                        <input
                                          type="number"
                                          min="1"
                                          value={getQtyInputValue(item)}
                                          onChange={(e) => handleLowQtyInputChange(item, e.target.value)}
                                        />
                                        {forecastEnabled && (() => {
                                          const suggested = getForecastSuggestion(item);
                                          if (!forecastLoading && suggested <= 0) return null;
                                          const title = (() => {
                                            const k = `${item?.clientId || 0}:${item?.itemId || 0}`;
                                            const s = forecastByKey[k];
                                            if (!s) return '';
                                            const lead = Number(s.leadTimeDays || 0);
                                            const targetDays = Number(s.targetDays || 0);
                                            const avg = Number(s.avgDailyUnits || 0);
                                            const sold = Number(s.unitsSold || 0);
                                            const onOrder = Number(s.onOrderUnits || 0);
                                            return [
                                              `Lead time: ${lead}d`,
                                              `Target coverage: ${targetDays}d`,
                                              `Avg daily demand: ${avg.toFixed(3)} units/day`,
                                              `Demand in lookback: ${sold.toFixed(2)} units (${forecastLookbackDays}d)`,
                                              `On order: ${onOrder} units`,
                                            ].join('\n');
                                          })();
                                          return (
                                            <div className="po-qty-hint" title={title}>
                                              {forecastLoading ? 'Forecasting…' : `Suggested: ${formatNumber(suggested)}`}
                                            </div>
                                          );
                                        })()}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      )
                    )}
                  </div>
                </section>
              </div>

              <div className="po-pane">
                <aside className="po-summary-card">
                  <div className="po-summary-header">
                    <FaClipboardList size={22} />
                    <div>
                      <h3>Purchase Plan</h3>
                      <p>Select SKUs to build an order.</p>
                    </div>
                  </div>

                  <div className="po-summary-metrics">
                    <div>
                      <span>Lines</span>
                      <strong>{planItems.length}</strong>
                    </div>
                    <div>
                      <span>Total Units</span>
                      <strong>{formatNumber(planTotals.totalQty)}</strong>
                    </div>
                    <div>
                      <span>Shortfall</span>
                      <strong>{formatNumber(planTotals.shortfall)}</strong>
                    </div>
                  </div>

                  <div className="po-plan-form">
                    <label>
                      <span>Warehouse</span>
                      <select value={planWarehouseId} onChange={(e) => setPlanWarehouseId(e.target.value)}>
                        <option value="">Select warehouse</option>
                        {warehouses.map((wh) => (
                          <option key={wh.id ?? wh.warehouseid} value={wh.id ?? wh.warehouseid}>
                            {wh.name || wh.description || `Warehouse #${wh.id ?? wh.warehouseid}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Supplier</span>
                      <select value={planSupplierId} onChange={(e) => setPlanSupplierId(e.target.value)}>
                        <option value="">Unassigned</option>
                        {suppliers.map((sup) => (
                          <option key={sup.id} value={sup.id}>
                            {sup.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Expected date</span>
                      <input type="date" value={planExpectedDate} onChange={(e) => setPlanExpectedDate(e.target.value)} />
                    </label>
                    <label className="span-2">
                      <span>Notes</span>
                      <textarea rows={3} value={planNotes} onChange={(e) => setPlanNotes(e.target.value)} />
                    </label>
                  </div>

                  <div className="po-summary-groups">
                    {planItems.length === 0 ? (
                      <div className="po-empty-summary">No SKUs in the plan yet.</div>
                    ) : (
                      <div className="po-summary-group">
                        <div className="po-summary-group-header">
                          <span>{planClient?.name || `Client #${planClientId}`}</span>
                          <span>{planItems.length} lines</span>
                        </div>
                        <ul>
                          {planItems.map((item) => (
                            <li key={`plan-${item.itemId}`}>
                              <div className="po-plan-line-left">
                                <button
                                  type="button"
                                  className="po-plan-remove"
                                  onClick={() => handleRemovePlanItem(item)}
                                  title="Remove from purchase plan"
                                  aria-label={`Remove ${item.sku || 'item'} from purchase plan`}
                                >
                                  <FaTrash size={14} />
                                </button>
                                <div className="po-plan-line-text">
                                  <strong>{item.sku}</strong>
                                  {item.description && <span className="mini">{item.description}</span>}
                                </div>
                              </div>
                              <div>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.qtyOrdered}
                                  onChange={(e) => handlePlanQtyChange(item.itemId, e.target.value)}
                                  style={{ width: 80, textAlign: 'right' }}
                                />
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {planError && <div className="po-error">{planError}</div>}

                  <div className="po-summary-actions">
                    <button className="btn primary" disabled={planSubmitDisabled} onClick={handleCreatePurchaseOrder}>
                      <FaClipboardCheck /> Add to Purchase Orders
                    </button>
                    <button className="btn secondary" disabled={planItems.length === 0} onClick={clearPlan}>
                      <FaUndo /> Clear Plan
                    </button>
                  </div>
                </aside>
              </div>
            </Split>
          </div>
          <div className="po-bottom-pane">
            <section className="po-table-card">
              <div className="po-table-header">
                <div>
                  <h2>Purchase Orders</h2>
                  <div className="po-table-stats">
                    <span>
                      <span className="po-stat-label">Total</span>
                      {formatNumber(poMeta?.total || 0)}
                    </span>
                  </div>
                </div>
                <div className="po-updated">{poLoading ? 'Refreshing…' : poUpdatedAt ? `Updated ${poUpdatedAt}` : ''}</div>
              </div>

              <div className="po-toolbar" style={{ marginBottom: '0.5rem' }}>
                <div className="po-search-field" style={{ flex: 1 }}>
                  <FaSearch />
                  <input
                    value={poSearch}
                    onChange={(e) => setPoSearch(e.target.value)}
                    placeholder="Search PO #, supplier, notes…"
                  />
                  {poSearch && (
                    <button
                      type="button"
                      className="po-search-clear"
                      onClick={() => setPoSearch('')}
                      aria-label="Clear PO search"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
                <label>
                  <span>Status</span>
                  <select value={poStatusFilter} onChange={(e) => setPoStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {PO_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                {showClientControls && (
                  <label>
                    <span>Client</span>
                    <select value={poClientFilter} onChange={(e) => setPoClientFilter(e.target.value)}>
                      <option value="">All</option>
                      {clientOptions.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  <span>Supplier</span>
                  <select value={poSupplierFilter} onChange={(e) => setPoSupplierFilter(e.target.value)}>
                    <option value="">All</option>
                    {suppliers.map((sup) => (
                      <option key={sup.id} value={sup.id}>{sup.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Warehouse</span>
                  <select value={poWarehouseFilter} onChange={(e) => setPoWarehouseFilter(e.target.value)}>
                    <option value="">All</option>
                    {warehouses.map((wh) => (
                      <option key={wh.id ?? wh.warehouseid} value={wh.id ?? wh.warehouseid}>
                        {wh.name || wh.description || `Warehouse #${wh.id ?? wh.warehouseid}`}
                      </option>
                    ))}
                  </select>
                </label>
                <button className="btn" onClick={() => fetchPurchaseOrders(poMeta.offset || 0)}>
                  <FaSync /> Refresh
                </button>
                <button className="btn secondary" onClick={resetPoColumns}>
                  <FaRedo style={{ marginRight: 6 }} /> Reset Columns
                </button>
              </div>

              {poError && <div className="po-error">{poError}</div>}

              <div className="po-table-scroll">
                {poLoading ? (
                  <div className="loading">Loading purchase orders…</div>
                ) : (
                  <table className="po-table" ref={poTableRef}>
                    <colgroup>
                      {activePoColumns.map((col, idx) => {
                        const isLast = idx === activePoColumns.length - 1;
                        const width = poColWidths[col];
                        return (
                          <col
                            key={col}
                            style={!isLast && width ? { width: `${width}px` } : undefined}
                          />
                        );
                      })}
                    </colgroup>
                    <thead>
                      <tr ref={poHeaderRowRef}>
                        {activePoColumns.map((key) => {
                          const label = PO_COLUMN_LABELS[key] || key;
                          const sortable = PO_SORTABLE_COLS.has(key);
                          const isSorted = sortable && poSortKey === key;
                          const sortIcon = isSorted
                            ? (poSortDir === 'asc' ? <FaSortUp size={12} /> : <FaSortDown size={12} />)
                            : <FaSort size={12} />;
                          const ariaSort = isSorted ? (poSortDir === 'asc' ? 'ascending' : 'descending') : 'none';
                          return (
                            <th key={key} data-colkey={key} aria-sort={sortable ? ariaSort : undefined}>
                              {sortable ? (
                                <button
                                  type="button"
                                  className={`po-sort-button${isSorted ? ' sorted' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePoSort(key);
                                  }}
                                  title={`Sort by ${label}`}
                                >
                                  <span>{label}</span>
                                  <span className="po-sort-icon">{sortIcon}</span>
                                </button>
                              ) : (
                                <span className="po-header-label">{label}</span>
                              )}
                              <span
                                className="col-resizer"
                                onMouseDown={(e) => startPoResize(key, e)}
                                onDoubleClick={() => autoFitPoCol(key)}
                              />
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {poList.length === 0 ? (
                        <tr>
                          <td colSpan={activePoColumns.length} className="po-empty">
                            No purchase orders found.
                          </td>
                        </tr>
                      ) : (
                        poList.map((po) => (
                            <tr key={po.id}>
                            <td data-colkey="actions" className="td-actions">
                              <div className="actions">
                                <button
                                  type="button"
                                  className="mini-btn"
                                  title="Print purchase order"
                                  aria-label="Print purchase order"
                                  onClick={(e) => { e.stopPropagation(); handlePrintPo(po); }}
                                >
                                  <FaPrint size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="mini-btn"
                                  title="Email purchase order"
                                  aria-label="Email purchase order"
                                  onClick={(e) => { e.stopPropagation(); handleEmailPo(po); }}
                                >
                                  <FaEnvelope size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="mini-btn"
                                  title="Export purchase order CSV"
                                  aria-label="Export purchase order CSV"
                                  onClick={(e) => { e.stopPropagation(); handleExportPoCsv(po); }}
                                >
                                  <FaFileCsv size={14} />
                                </button>
                                {canDeletePo(po) && (
                                  <button
                                    type="button"
                                    className="mini-btn danger"
                                    title="Delete purchase order"
                                    aria-label="Delete purchase order"
                                    onClick={(e) => { e.stopPropagation(); openDeleteModal(po); }}
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td data-colkey="poNumber">{po.poNumber || `PO-${po.id}`}</td>
                            {showClientControls && (
                              <td data-colkey="client">
                                <div className="po-client-cell">
                                  <div>
                                    <div className="po-client-name">{po.clientName || `Client #${po.clientId}`}</div>
                                    <span className="po-muted">#{po.clientId}</span>
                                  </div>
                                </div>
                              </td>
                            )}
                            <td data-colkey="supplier">{po.supplierName || '—'}</td>
                            <td data-colkey="warehouse">
                              <div className="po-client-cell">
                                <FaWarehouse size={14} style={{ opacity: 0.7 }} />
                                <div>{po.warehouseName || 'Unassigned'}</div>
                              </div>
                            </td>
                            <td data-colkey="notes">
                              <div className="po-notes-cell" title={po.notes || '—'}>
                                {po.notes || '—'}
                              </div>
                            </td>
                            <td data-colkey="lineCount">{formatNumber(po.lineCount)}</td>
                            <td data-colkey="totalQty">{formatNumber(po.totalQty)}</td>
                            <td data-colkey="totalReceived">{formatNumber(po.totalReceived)}</td>
                            <td data-colkey="status">
                              <select
                                value={po.status}
                                onChange={(e) => handleUpdateStatus(po, e.target.value)}
                                disabled={!!poSaving[po.id] || Number(po.status) === 4}
                              >
                                {PO_STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </td>
                            <td data-colkey="expectedDate">
                              {po.expectedDate ? new Date(po.expectedDate).toLocaleDateString() : '—'}
                            </td>
                            <td data-colkey="updatedAt">
                              {po.updatedAt ? new Date(po.updatedAt).toLocaleString() : '—'}
                            </td>
                            <td data-colkey="convert" className="td-actions">
                              <div className="actions">
                                {po.status !== 4 && (
                                  <button
                                    className="mini-btn primary"
                                    disabled={!!poSaving[po.id]}
                                    onClick={() => handleConvertPurchaseOrder(po)}
                                    title="Convert to receipt"
                                  >
                                    <FaClipboardCheck />
                                  </button>
                                )}
                              </div>
                            </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="suppliers-pagination" style={{ marginTop: '0.5rem' }}>
                <button className="btn" disabled={(poMeta.offset || 0) === 0} onClick={() => handlePoPageChange(-1)}>
                  Prev
                </button>
                <span>
                  Showing {formatNumber(Math.min(poMeta.total || 0, (poMeta.offset || 0) + (poMeta.limit || 25)))} of {formatNumber(poMeta.total || 0)}
                </span>
                <button
                  className="btn"
                  disabled={(poMeta.offset || 0) + (poMeta.limit || 25) >= (poMeta.total || 0)}
                  onClick={() => handlePoPageChange(1)}
                >
                  Next
                </button>
              </div>
            </section>
          </div>
        </Split>
      </div>

      {emailModalOpen && (
        <div className="po-email-overlay" onClick={closeEmailModal}>
          <div
            className="po-email-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="po-email-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="po-email-header">
              <div>
                <h3 id="po-email-title">Email Purchase Order</h3>
                <p>{emailTargetPo ? formatPoLabel(emailTargetPo) : ''}</p>
              </div>
              <button className="po-email-close" onClick={closeEmailModal} aria-label="Close email modal" disabled={poEmailSending}>
                ×
              </button>
            </div>
            <div className="po-email-body">
              <label>
                <span>To</span>
                <input
                  type="email"
                  value={poEmailFields.to}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, to: e.target.value }))}
                  disabled={poEmailSending}
                  placeholder="supplier@example.com"
                />
              </label>
              <label>
                <span>CC</span>
                <input
                  type="text"
                  value={poEmailFields.cc}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, cc: e.target.value }))}
                  disabled={poEmailSending}
                  placeholder="optional"
                />
              </label>
              <label>
                <span>BCC</span>
                <input
                  type="text"
                  value={poEmailFields.bcc}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, bcc: e.target.value }))}
                  disabled={poEmailSending}
                  placeholder="optional"
                />
              </label>
              <label>
                <span>Subject</span>
                <input
                  type="text"
                  value={poEmailFields.subject}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, subject: e.target.value }))}
                  disabled={poEmailSending}
                />
              </label>
              <label>
                <span>Message</span>
                <textarea
                  rows={6}
                  value={poEmailFields.message}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, message: e.target.value }))}
                  disabled={poEmailSending}
                />
              </label>
              <label className="po-email-checkbox">
                <input
                  type="checkbox"
                  checked={poEmailFields.includeCsv}
                  disabled={poEmailSending}
                  onChange={(e) => setPoEmailFields((prev) => ({ ...prev, includeCsv: e.target.checked }))}
                />
                <span>Attach CSV export</span>
              </label>
            </div>
            <div className="po-email-footer">
              <button className="btn secondary" onClick={closeEmailModal} disabled={poEmailSending}>
                Cancel
              </button>
              <button className="btn primary" onClick={submitEmailPo} disabled={poEmailSending}>
                {poEmailSending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {convertModalOpen && (
        <div className="po-convert-overlay" onClick={closeConvertModal}>
          <div
            className="po-convert-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="po-convert-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="po-convert-header">
              <div>
                <h3 id="po-convert-title">Convert Purchase Order</h3>
                <p className="po-convert-subtitle">
                  PO {convertModalPo?.poNumber || convertModalPo?.id} ·{' '}
                  {convertModalLines.length} line{convertModalLines.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                className="po-convert-close"
                onClick={closeConvertModal}
                disabled={convertModalSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="po-convert-body">
              <p className="po-convert-note">
                Review each line and enter the actual units received. Adjustments are saved on the
                receipt before finalizing.
              </p>
              {convertModalError && <div className="po-error">{convertModalError}</div>}
              {convertModalLoading ? (
                <div className="loading" style={{ padding: '1rem 0' }}>Loading purchase order…</div>
              ) : convertModalLines.length === 0 ? (
                <div className="po-empty" style={{ marginTop: '1rem' }}>
                  No line items found on this purchase order.
                </div>
              ) : (
                <>
                  <div className="po-convert-summary">
                    <div>
                      <span>Ordered</span>
                      <strong>{formatNumber(convertTotals.ordered)}</strong>
                    </div>
                    <div>
                      <span>Previously received</span>
                      <strong>{formatNumber(convertTotals.prevReceived)}</strong>
                    </div>
                    <div>
                      <span>Outstanding</span>
                      <strong>{formatNumber(convertTotals.outstanding)}</strong>
                    </div>
                    <div>
                      <span>Actual this receipt</span>
                      <strong>{formatNumber(convertTotals.actual)}</strong>
                    </div>
                  </div>
                  {(convertHasShortages || convertHasOverages) && (
                    <div className="po-convert-flags">
                      {convertHasShortages && <span className="short-flag">Short quantities detected</span>}
                      {convertHasOverages && <span className="over-flag">Overages entered</span>}
                    </div>
                  )}
                  <div className="po-convert-lines">
                    <table>
                      <thead>
                        <tr>
                          <th>SKU / Description</th>
                          <th>Ordered</th>
                          <th>Prev&nbsp;Recv</th>
                          <th>Outstanding</th>
                          <th>Actual Received</th>
                          <th>Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {convertModalLines.map((line) => {
                          const outstanding = Number(line.outstanding) || 0;
                          const actual = Number(line.actualQty) || 0;
                          const diff = actual - outstanding;
                          const diffClass = diff === 0 ? '' : diff < 0 ? 'short' : 'over';
                          return (
                            <tr key={line.lineId}>
                              <td>
                                <div className="po-convert-sku">
                                  <strong>{line.sku}</strong>
                                  {line.description && <span>{line.description}</span>}
                                </div>
                              </td>
                              <td>{formatNumber(line.orderedQty)}</td>
                              <td>{formatNumber(line.prevReceived)}</td>
                              <td>{formatNumber(outstanding)}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max={line.outstanding}
                                  value={actual}
                                  onChange={(e) => handleConvertQtyChange(line.lineId, e.target.value)}
                                  disabled={convertModalSaving || line.outstanding <= 0}
                                />
                              </td>
                              <td className={`po-convert-diff ${diffClass}`}>
                                {diff === 0
                                  ? 'On target'
                                  : diff < 0
                                  ? `${formatNumber(Math.abs(diff))} short`
                                  : `${formatNumber(diff)} over`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="po-convert-footer">
              <button className="btn secondary" onClick={closeConvertModal} disabled={convertModalSaving}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={submitConvertModal}
                disabled={convertModalSaving || !convertHasQty}
              >
                {convertModalSaving ? 'Converting…' : 'Create Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div className="po-delete-overlay" onClick={closeDeleteModal}>
          <div
            className="po-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="po-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="po-delete-title">Delete Purchase Order</h3>
            <p className="po-delete-text">
              Are you sure you want to delete <strong>{formatPoLabel(deleteTargetPo)}</strong>?
            </p>
            <p className="po-delete-warning">This action cannot be undone.</p>
            {deleteModalError && <div className="po-error">{deleteModalError}</div>}
            <div className="po-delete-actions">
              <button className="btn secondary" onClick={closeDeleteModal} disabled={deleteModalLoading}>
                Cancel
              </button>
              <button className="btn danger" onClick={handleConfirmDelete} disabled={deleteModalLoading}>
                {deleteModalLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {forecastHelpTopic && (() => {
        const help = forecastHelpContent[forecastHelpTopic];
        if (!help) return null;
        return (
          <div className="po-help-overlay" onClick={() => setForecastHelpTopic('')}>
            <div
              className="po-help-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="po-help-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="po-help-header">
                <h3 id="po-help-title">{help.title}</h3>
                <button
                  type="button"
                  className="po-help-close"
                  onClick={() => setForecastHelpTopic('')}
                  aria-label="Close"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="po-help-body">
                {help.body.map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
              <div className="po-help-footer">
                <button type="button" className="btn primary" onClick={() => setForecastHelpTopic('')}>
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
